import {
  FundedProject,
  Milestone,
  PaginatedResult,
  Payment,
  SFAutoFields,
  SunstoneDocument,
} from '../../../../../types/api';
import {
  FEATURE_FLAGS,
  isFeatureEnabled,
} from '../../components/account/userSettings/DebugForm';

import {
  FUNDED_PROJECTS_URL,
  INSTALLER_DASHBOARD_FUNDED_PROJECTS_URL,
  INSTALLER_DASHBOARD_PROJECTS_SUMMARY_URL,
  MILESTONE_STATUSES,
  MILESTONE_TYPES,
} from '../../constants';
import getErrorFromErrorResponse from '../../utils/errors/getErrorFromErrorResponse';
import { api } from './API';

export type PaginatedProjectsParams = {
  page?: number;
  size: number;
  completed?: boolean | null;
  search?: string | null;
  project_status?: FundedProject['external_status'];
  active_milestone?: FundedProject['active_milestone'] | null;
};
export async function getFundedProjects(params: PaginatedProjectsParams) {
  const response = await api().get<PaginatedResult<FundedProject>>(
    FUNDED_PROJECTS_URL,
    { params }
  );
  return response.data;
}

export async function getInstallerDashboardFundedProjects(
  accountId: string,
  accountType: string
) {
  const response = await api().get<any>(
    INSTALLER_DASHBOARD_FUNDED_PROJECTS_URL,
    {
      params: { accountId, accountType },
    }
  );
  return response.data;
}

export async function getInstallerDashboardProjectsSummary(
  accountId: string,
  accountType: string
) {
  const response = await api().get<any>(
    INSTALLER_DASHBOARD_PROJECTS_SUMMARY_URL,
    {
      params: { accountId, accountType },
    }
  );
  return response.data;
}

export async function getFundedProjectById(id: string) {
  const response = await api().get<FundedProject>(
    `${FUNDED_PROJECTS_URL}/${id}`
  );
  return response.data;
}

export async function optInToAdvanceFunding(
  projectId: string,
  typeOfAdvanceFunding: FundedProject['type_of_advance_funding'],
  selectedAdvancedFunding: FundedProject['selected_advanced_funding']
) {
  try {
    const greentechPayload: Pick<
      FundedProject,
      'type_of_advance_funding' | 'selected_advanced_funding'
    > = {
      type_of_advance_funding: typeOfAdvanceFunding,
      selected_advanced_funding: selectedAdvancedFunding,
    };
    const response = await api().patch<FundedProject>(
      `${FUNDED_PROJECTS_URL}/${projectId}`,
      greentechPayload
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

/** Convert an EO dollar amount into a % of the loan (not % of the EO bucket). */
export function dollarsToEoLoanPct(
  dollars: number,
  originalEoPool: number,
  eoProgramPct: number
): number {
  return originalEoPool > 0 ? (dollars / originalEoPool) * eoProgramPct : 0;
}

export function formatEOSidebarTitle(
  requestNumber: number,
  allocatedLoanPct: number
): string {
  return `Equipment Order ${requestNumber} (${allocatedLoanPct.toFixed(0)}%)`;
}

export function isEoMilestoneDone(milestone: Milestone): boolean {
  return Boolean(
    milestone.completion_date ||
      milestone.status === MILESTONE_STATUSES.PAID ||
      milestone.status === MILESTONE_STATUSES.COMPLETED
  );
}

export async function getFundedProjectMilestones(id: string) {
  const response = await api().get<Milestone[]>(
    `${FUNDED_PROJECTS_URL}/${id}/milestones`
  );

  const showMultipleEoMilestones = isFeatureEnabled(
    FEATURE_FLAGS.multipleEoMilestones
  );
  const eoType = MILESTONE_TYPES.EQUIPMENT_ORDER;

  if (
    !showMultipleEoMilestones ||
    response.data.filter(({ type }) => type === eoType).length === 0
  ) {
    return response.data;
  }

  const eos: Milestone[] = response.data
    .filter((milestone) => milestone.type === eoType)
    .sort((a, b) => {
      // Sort by sort_id, then by created_date, so that EO #1 always comes first
      const sortIdA = a.sort_id ?? '';
      const sortIdB = b.sort_id ?? '';
      if (!sortIdA && sortIdB) return 1;
      if (sortIdA && !sortIdB) return -1;
      const bySortId = sortIdA.localeCompare(sortIdB);
      if (bySortId !== 0) return bySortId;
      return (a.created_date ?? '').localeCompare(b.created_date ?? '');
    });

  const otherMilestones = response.data.filter(
    (milestone) => milestone.type !== eoType
  );

  // Which EO slots to show in the sidebar:
  // - Always include submitted slots (requested_amount is set)
  // - Include the next pending slot only after the last submitted EO is done
  const submittedEOs = eos.filter((m) => m.requested_amount !== null);
  const pendingEOs = eos.filter((m) => m.requested_amount === null);

  const lastSubmitted = submittedEOs[submittedEOs.length - 1];
  const showNextPendingSlot =
    pendingEOs.length > 0 &&
    (submittedEOs.length === 0 ||
      Boolean(lastSubmitted && isEoMilestoneDone(lastSubmitted)));

  const visibleEOs = showNextPendingSlot
    ? [...submittedEOs, pendingEOs[0]]
    : submittedEOs;

  // EO1 max is the original dollar cap. Do not sum later-slot leftover.
  const originalEoPool = Number(eos[0]?.max_allowed_amount ?? 0);

  const annotatedEOs: Milestone[] = visibleEOs.map((eo, index) => ({
    ...eo,
    title: formatEOSidebarTitle(index + 1, Number(eo.payment_percentage ?? 0)),
    total_eos: eos.length,
    total_eo_pool: originalEoPool,
    eo_program_pct: Number(eos[0]?.payment_percentage ?? 0),
  }));

  return [...otherMilestones, ...annotatedEOs];
}

export type UpdateRequiredMilestoneDocumentVariables = {
  documentId: string;
  milestoneId: string;
  projectId: string;
  updates: Partial<Omit<SunstoneDocument, SFAutoFields>>;
};
export async function updateRequiredDocument(
  variables: UpdateRequiredMilestoneDocumentVariables
) {
  const { documentId, milestoneId, projectId, updates } = variables;
  try {
    const response = await api().patch<SunstoneDocument>(
      `${FUNDED_PROJECTS_URL}/${projectId}/milestones/${milestoneId}/documents/${documentId}`,
      updates
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type UploadRequiredMilestoneDocumentVariables = {
  documentId: string;
  milestoneId: string;
  projectId: string;
  data: FormData;
};
export async function uploadRequiredDocument({
  documentId,
  milestoneId,
  projectId,
  data,
}: UploadRequiredMilestoneDocumentVariables) {
  try {
    const response = await api({
      timeout: 10 * 60 * 1000,
    }).put<SunstoneDocument>(
      `${FUNDED_PROJECTS_URL}/${projectId}/milestones/${milestoneId}/documents/${documentId}`,
      data,
      {
        timeout: 10 * 60 * 1000,
      }
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type SubmitChecklistAnswersVariables = {
  projectId: string;
  milestoneId: string;
  documentId: string;
  answers: Record<string, string>;
  file_names?: string[];
};

export async function submitChecklistAnswers({
  projectId,
  milestoneId,
  documentId,
  answers,
  file_names,
}: SubmitChecklistAnswersVariables) {
  try {
    const response = await api().post(
      `${FUNDED_PROJECTS_URL}/${projectId}/milestones/${milestoneId}/documents/${documentId}/checklist-answers`,
      file_names ? { answers, file_names } : { answers }
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type EquipmentBrands = {
  modules: string[];
  inverters: string[];
  batteries: string[];
};

export async function getProjectEquipmentBrands(projectId: string) {
  try {
    const response = await api().get<EquipmentBrands>(
      `${FUNDED_PROJECTS_URL}/${projectId}/equipment-brands`
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type UploadAdditionalMilestoneDocumentVariables = {
  projectId: string;
  milestoneId: string;
  data: FormData;
};
export async function uploadAdditionalMilestoneDocument({
  data,
  milestoneId,
  projectId,
}: UploadAdditionalMilestoneDocumentVariables) {
  try {
    const response = await api({
      timeout: 10 * 60 * 1000,
    }).post<SunstoneDocument>(
      `${FUNDED_PROJECTS_URL}/${projectId}/milestones/${milestoneId}/documents`,
      data,
      {
        timeout: 10 * 60 * 1000,
      }
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type UploadProjectFileVariables = {
  projectId: string;
  documentType: string;
  file: File;
};

/**
 * Persist a file to Railway `los.files` for Oracle analysis.
 * Creates a Railway RequiredDocument parent, not a Salesforce / borrower-checklist document.
 * Returns `{ id, requiredDocumentId }` for analyze / insights polling and later PATCH/DELETE.
 */
export async function uploadProjectFile({
  projectId,
  documentType,
  file,
}: UploadProjectFileVariables) {
  const data = new FormData();
  data.append('file', file);
  data.append('document_type', documentType);

  try {
    const response = await api({
      timeout: 10 * 60 * 1000,
    }).post<{ id: string; requiredDocumentId?: string }>(
      `${FUNDED_PROJECTS_URL}/${projectId}/files`,
      data,
      {
        timeout: 10 * 60 * 1000,
      }
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type UpdateProjectRequiredDocumentVariables = {
  projectId: string;
  requiredDocumentId: string;
  requirement_name?: string;
  required_for_step?: string;
  sunstone_document_id?: string;
};

export async function updateProjectRequiredDocument({
  projectId,
  requiredDocumentId,
  ...payload
}: UpdateProjectRequiredDocumentVariables) {
  try {
    const response = await api().patch(
      `${FUNDED_PROJECTS_URL}/${projectId}/required-documents/${requiredDocumentId}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type DeleteProjectRequiredDocumentVariables = {
  projectId: string;
  requiredDocumentId: string;
};

export async function deleteProjectRequiredDocument({
  projectId,
  requiredDocumentId,
}: DeleteProjectRequiredDocumentVariables) {
  try {
    await api().delete(
      `${FUNDED_PROJECTS_URL}/${projectId}/required-documents/${requiredDocumentId}`
    );
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type FileInsight = {
  id?: string;
  file_id?: string;
  output?: {
    output?: Array<{ id?: string; status?: string; value?: string }>;
  };
};

export type StartFileAnalysisVariables = {
  projectId: string;
  fileId: string;
};

/**
 * Fire-and-forget Oracle analyze for an existing Railway file.
 * Does not wait for OCR/LLM; poll insights by file id afterward.
 */
export async function startFileAnalysis({
  projectId,
  fileId,
}: StartFileAnalysisVariables) {
  try {
    await api().post(
      `${FUNDED_PROJECTS_URL}/${projectId}/files/${fileId}/analyze`
    );
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type GetFileInsightsVariables = {
  projectId: string;
  fileId: string;
};

/**
 * Fetch the Railway oracle_insights row for a file.
 * Returns null if none exists yet so callers can poll.
 */
export async function getFileInsights({
  projectId,
  fileId,
}: GetFileInsightsVariables): Promise<FileInsight | null> {
  try {
    const response = await api().get<FileInsight>(
      `${FUNDED_PROJECTS_URL}/${projectId}/files/${fileId}/insights`
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw getErrorFromErrorResponse(error);
  }
}

export type MilestoneUpdateVariables = {
  projectId: string;
  milestoneId: string;
  update: Partial<
    Pick<
      Milestone,
      | 'value_1'
      | 'requested_amount'
      | 'status'
      | 'equipment_type'
      | 'title'
      | 'estimated_milestone_date'
    >
  >;
};

export async function updateMilestone({
  milestoneId,
  projectId,
  update,
}: MilestoneUpdateVariables) {
  try {
    const response = await api().patch<Milestone>(
      `${FUNDED_PROJECTS_URL}/${projectId}/milestones/${milestoneId}`,
      update
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type UpdateEquipmentOrderMilestoneVariables = {
  projectId: string;
  milestoneId: string;
  data: Partial<Milestone>;
};

export async function updateEquipmentOrderMilestone({
  milestoneId,
  projectId,
  data,
}: UpdateEquipmentOrderMilestoneVariables) {
  try {
    const response = await api().patch<Milestone>(
      `${FUNDED_PROJECTS_URL}/${projectId}/eo-milestones/${milestoneId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type CompleteEquipmentOrderMilestoneVariables = {
  projectId: string;
};

export async function completeEquipmentOrderMilestone({
  projectId,
}: CompleteEquipmentOrderMilestoneVariables) {
  try {
    const response = await api().post<Milestone>(
      `${FUNDED_PROJECTS_URL}/${projectId}/eo-milestones-complete`
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type MarkEOMilestoneReadyVariables = {
  projectId: string;
  milestoneId: string;
};

export async function markEOMilestoneReady({
  projectId,
  milestoneId,
}: MarkEOMilestoneReadyVariables) {
  try {
    const response = await api().patch(
      `${FUNDED_PROJECTS_URL}/${projectId}/eo-milestones/${milestoneId}/mark-ready`
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export type RecalculateEOAmountVariables = {
  projectId: string;
  milestoneId: string;
};

export type RecalculatedMilestoneAmounts = {
  id: string;
  requested_amount: string | null;
  payment_amount: string | null;
  payment_percentage: string | null;
  max_allowed_amount: string | null;
};

export type RecalculateEOAmountResponse = {
  detail: string;
  requested_amount: string;
  milestones: RecalculatedMilestoneAmounts[];
};

export async function recalculateEOAmount({
  projectId,
  milestoneId,
}: RecalculateEOAmountVariables) {
  try {
    const response = await api().post<RecalculateEOAmountResponse>(
      `${FUNDED_PROJECTS_URL}/${projectId}/eo-milestones/${milestoneId}/recalculate-amount`
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}

export async function getFundedProjectPayments(projectId: string) {
  try {
    const response = await api().get<Payment[]>(
      `${FUNDED_PROJECTS_URL}/${projectId}/payments`
    );
    return response.data;
  } catch (error) {
    throw getErrorFromErrorResponse(error);
  }
}
